// Os três campos que todo serviço do catálogo precisa: Área (uma das 6 da
// empresa), Sub-área (obrigatória quando a área tem) e Código de serviço da NF.
// Usado no cadastro do catálogo, no cadastro rápido das composições e na aba
// "Em aberto". Todos os menus filtram por escrita, sem depender de acento.
import { useState } from 'react';
import { Button, Input, Select, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  OPCOES_AREA, aoTrocarArea, casaBusca, exigeSubarea, subareasDaArea, type EscolhaArea, type Subarea,
} from '../../utils/areasEmpresa';
import { OPCOES_CNAE } from '../../utils/cnaes';

const filtro = (digitado: string, opcao?: { label?: unknown }) => casaBusca(digitado, opcao?.label);

export function CamposAreaServico({ valor, onChange, subareas, onCriarSubarea, manterSubarea, tamanho, larguras, popupNoPai, desabilitado }: {
  valor: EscolhaArea;
  onChange: (v: EscolhaArea) => void;
  subareas: Subarea[];
  // Cria a sub-área e devolve a nova (ou lança o erro, que aparece no menu).
  onCriarSubarea?: (setor: string, nome: string) => Promise<Subarea>;
  manterSubarea?: string | null;        // sub-área que o serviço já usa (fica na lista mesmo desativada)
  tamanho?: 'small' | 'middle';
  larguras?: { area?: number | string; subarea?: number | string; cnae?: number | string };
  popupNoPai?: boolean;                 // menu dentro do próprio modal (modais com overlay próprio)
  desabilitado?: boolean;
}) {
  const [novoNome, setNovoNome] = useState('');
  const [criando, setCriando] = useState(false);
  const [erroCriar, setErroCriar] = useState<string | null>(null);
  const opcoesSub = subareasDaArea(subareas, valor.setor_chave, manterSubarea)
    .map(s => ({ value: s.id, label: s.ativo ? s.nome : `${s.nome} (desativada)` }));
  const obrigatoria = exigeSubarea(subareas, valor.setor_chave);
  const container = popupNoPai ? ((n: HTMLElement) => n.parentElement as HTMLElement) : undefined;

  async function criar() {
    const nome = novoNome.trim();
    if (criando || !nome || !valor.setor_chave || !onCriarSubarea) return;
    setCriando(true); setErroCriar(null);
    try {
      const nova = await onCriarSubarea(valor.setor_chave, nome);
      setNovoNome('');
      onChange({ ...valor, subarea_id: nova.id });
    } catch (e: any) {
      setErroCriar(e?.message || 'Não consegui criar a sub-área.');
    } finally { setCriando(false); }
  }

  return (
    <>
      <Select
        aria-label="Área" size={tamanho} disabled={desabilitado}
        value={valor.setor_chave || undefined}
        onChange={(v) => onChange(aoTrocarArea(valor, v || null))}
        options={OPCOES_AREA} placeholder="Área da empresa"
        showSearch filterOption={filtro} allowClear
        status={!valor.setor_chave ? 'warning' : undefined}
        popupMatchSelectWidth={false}
        getPopupContainer={container}
        style={{ width: larguras?.area ?? '100%' }}
      />
      <Select
        aria-label="Sub-área" size={tamanho}
        disabled={desabilitado || !valor.setor_chave}
        value={valor.subarea_id || undefined}
        onChange={(v) => onChange({ ...valor, subarea_id: v || null })}
        options={opcoesSub}
        placeholder={!valor.setor_chave ? 'Escolha a área primeiro' : (obrigatoria ? 'Sub-área' : 'Esta área não tem sub-áreas')}
        showSearch filterOption={filtro} allowClear
        status={obrigatoria && !valor.subarea_id ? 'warning' : undefined}
        notFoundContent={valor.setor_chave ? 'Nenhuma sub-área nesta área.' : null}
        getPopupContainer={container}
        style={{ width: larguras?.subarea ?? '100%' }}
        popupRender={(menu) => (
          <>
            {menu}
            {/* Só em área que JÁ tem sub-área: a PRIMEIRA sub-área de uma área faz todos os
                serviços dela passarem a exigir sub-área — isso se cria em "Áreas e sub-áreas",
                com o aviso, e não por um clique no menu. */}
            {onCriarSubarea && valor.setor_chave && obrigatoria && (
              <div style={{ borderTop: '1px solid #F0F1F5', padding: 8 }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    size="small" value={novoNome} maxLength={80} placeholder="Nova sub-área"
                    aria-label="Nome da nova sub-área"
                    onChange={e => setNovoNome(e.target.value)}
                    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') criar(); }}
                  />
                  <Button size="small" type="primary" icon={<PlusOutlined />} loading={criando}
                    disabled={!novoNome.trim()} onClick={criar}>Criar</Button>
                </Space.Compact>
                {erroCriar && <div style={{ color: '#B91626', fontSize: 12, marginTop: 4 }}>{erroCriar}</div>}
              </div>
            )}
          </>
        )}
      />
      <Select
        aria-label="Código de serviço da NF (CNAE)" size={tamanho} disabled={desabilitado}
        value={valor.cnae || undefined}
        onChange={(v) => onChange({ ...valor, cnae: v || null })}
        options={OPCOES_CNAE} placeholder="Código de serviço da NF"
        showSearch filterOption={filtro} allowClear
        status={!valor.cnae ? 'warning' : undefined}
        popupMatchSelectWidth={320}
        getPopupContainer={container}
        style={{ width: larguras?.cnae ?? '100%' }}
      />
    </>
  );
}
